import { Subject, BehaviorSubject, Subscription, merge } from 'rxjs';
import { takeUntil, switchMap, filter, finalize } from 'rxjs/operators';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  ViewChild,
  ElementRef,
  forwardRef,
  TemplateRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpEventType } from '@angular/common/http';
import {
  PhxFile,
  PHX_FILE_TYPE,
  PHX_FILE_FORMAT,
  PHX_FILE_FORMAT_MIME,
  PHX_TYPE_DEFAULT_FORMATS,
  PHX_IMAGE_MIME_MAPPINGS,
  PHX_LAYOUT_TYPE,
  PhxLayoutType,
  PhxUploadURLProviderFn,
  PHX_FILE_LOADING_STATUS,
  BYTES_IN_MB,
  DEFAULT_MAX_FILE_SIZE_MB,
  PhxFileRejection,
  PhxFileErrorCode,
  PhxFileEventPayload,
} from './phx-file-upload.types';
import { PhxGalleryImage } from '../gallery/phx-gallery.type';
import { Bullet16, Plus, ChevronLeft, ChevronRight, Image, Video, Document } from '../icon/icon-map';
import { PhxFileUploadS3Service } from './phx-file-upload-s3.service';
import { generateUuid } from '../../utils';
import { PhxTranslatePipe } from '../../translation/phx-translation.pipe';
@Component({
  selector: 'phx-file-upload',
  templateUrl: './phx-file-upload.component.html',
  styleUrls: ['./phx-file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxFileUploadComponent),
      multi: true,
    },
  ],
  host: {
    class: 'phx-file-upload',
    '[class.phx-file-upload-edit-mode]': "phxEditMode === true",
    '[class.phx-file-upload-inline]': "phxLayout === PHX_LAYOUT_TYPE.INLINE_NO_DETAIL",
    '[class.phx-file-upload-error]': "showErrorBanner",
    '(blur)': 'onBlur()',
  },
})
export class PhxFileUploadComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  /** Array of files to display */
  @Input() public phxFiles: PhxFile[] = [];

  /**
   * TILES layout only. When true (default), clicking an empty placeholder tile opens the native
   * file picker so the chosen file flows through the normal upload pipeline. When false, only
   * `phxTileCapture` is emitted and the consumer can show its own capture UI.
   */
  @Input() public phxShowPickerOnTileClick = true;

  /** When true, hides the upload affordance (drop zone and upload button) across all layouts. */
  @Input() public phxHideUploadContainer = false;

  /**
   * TILES layout only. Optional when provided, a positive banner
   * is shown once the capture is complete (every tile up to phxFileUploadLimit filled, all DONE, none
   * in error). Leave unset to show no success banner.
   */
  @Input() public phxTilesSuccessMessage?: string;

  /**
   * TILES layout only. Optional, When unset, a default title is used.
   */
  @Input() public phxTilesErrorTitle?: string;

  /** Layout type for the file upload component */
  @Input() public phxLayout: PhxLayoutType = PHX_LAYOUT_TYPE.WRAPPED;

  /** Whether to show file details */
  @Input() public phxShowDetails = true;

  /** Whether to obfuscate file information */
  @Input() public phxObfuscate = false;

  /** Whether to upload files immediately upon selection */
  @Input() public phxUploadOnSelect = false;

  /** Function that provides the upload URL for files */
  @Input() public phxUploadURLProvider?: PhxUploadURLProviderFn;

  /** Maximum file size in MB */
  @Input() public phxFileMaxSize = DEFAULT_MAX_FILE_SIZE_MB;

  /** Types of files allowed for upload */
  @Input() public phxFilesTypesAllowed: PHX_FILE_TYPE = PHX_FILE_TYPE.DOCUMENTS;

  /**
   * Optional explicit list of formats to accept. When provided, it is authoritative and fully
   * defines the accepted formats, overriding phxFilesTypesAllowed and allowing formats to be mixed
   * across categories (e.g. [PDF, MP4]). When omitted, all of the phxFilesTypesAllowed default
   * formats are accepted.
   */
  @Input() public phxAllowedFormats?: PHX_FILE_FORMAT[];

  /** Interaction Configuration */

  /** Whether edit mode is enabled */
  @Input() public phxEditMode = false;

  /** Whether multiple files can be selected in one go */
  @Input() public phxMultiple = true;

  /** Maximum number of files allowed to be uploaded */
  @Input() public phxFileUploadLimit?: number;

  /** Width of file thumbnail in pixels (applies when phxShowDetails is false or phxEditMode is false) */
  @Input() public phxThumbnailWidth = 56;

  /** Height of file thumbnail in pixels (applies when phxShowDetails is false or phxEditMode is false) */
  @Input() public phxThumbnailHeight = 56;

  /** Text displayed in the drop zone. If not provided, a translated default will be used. */
  @Input() public phxDropText: string;

  /** Text displayed on the upload button. If not provided, a translated default will be used. */
  @Input() public phxUploadBtnText: string;

  /** Heading template for the component */
  @Input() public phxHeading?: TemplateRef<any>;

  /** Subtext template for the component */
  @Input() public phxSubtext?: TemplateRef<any>;

  /** Custom template for the upload button. When provided, replaces the default button. */
  @Input() public phxUploadButtonTemplate?: TemplateRef<any>;

  /** Currently active/selected file to highlight in the inline layout */
  @Input() public phxActiveFile?: PhxFile;

  /** Bulk upload mode */
  @Input() public phxFileBulkUploadMode: boolean = false;

  /** When true, the file count text shows batch progress ("X of Y uploaded") instead of a simple count ("X files uploaded"). */
  @Input() public phxShowBatchProgress: boolean = false;

  /** Fires when a file finishes a successful upload (S3 PUT finished & status moved to DONE) */
  @Output() public phxUploadComplete = new EventEmitter<PhxFile>();

  /** Fires when the file list changes: add, remove, or a file status transitions (not on every progress byte) */
  @Output() public phxFilesChange = new EventEmitter<PhxFile[]>();

  /** Fires when the component accepts a file (via drop or selection). Will not emit when file gets rejected due to larger size or unsupported format */
  @Output() public phxFileSelected = new EventEmitter<PhxFile>();

  /** Fires when the user presses the delete button (cross) */
  @Output() public phxDeleteFile = new EventEmitter<PhxFileEventPayload>();

  /** Fires when the user updates a file (such as rotating the file) */
  @Output() public phxFileUpdated = new EventEmitter<PhxFileEventPayload>();

  /** Fires on any error state transition (validation failure added inline, upload failure, or unsupported/size rejection) */
  @Output() public phxFileError = new EventEmitter<PhxFile>();

  @Output() public phxFileClicked = new EventEmitter<PhxFileEventPayload>();

  /**
   * Fires when the user retries a failed upload
   */
  @Output() public phxFileRetry = new EventEmitter<PhxFileEventPayload>();

  /**
   * Fires when an empty capture tile is clicked in the TILES layout, with the slot index.
   * Consumers can use this for custom capture UI. The consumer can subscribe to this event and open the native file picker themselves.
   * Just make sure the phxShowPickerOnTileClick is set to false to avoid opening the native file picker twice.
   */
  @Output() public phxTileCapture = new EventEmitter<{ index: number }>();

  /** Hidden native file input, triggered programmatically for the TILES picker-on-click flow. */
  @ViewChild('fileInput') private _fileInput?: ElementRef<HTMLInputElement>;

  public isDragOver = false;
  public readonly PHX_LAYOUT_TYPE = PHX_LAYOUT_TYPE;
  public readonly bulletIcon = Bullet16;
  public readonly plusIcon = Plus;
  public readonly leftIcon = ChevronLeft;
  public readonly rightIcon = ChevronRight;
  public readonly imageTypes = Object.values(PHX_IMAGE_MIME_MAPPINGS).join(',');


  public isGalleryOpen = false;
  public gallerySelectedIndex = 0;
  public galleryImages: PhxGalleryImage[] = [];
  public disabled = false;

  /** Cached tile slots for the TILES layout (recomputed when files or limit change). */
  public tileSlots: (PhxFile | null)[] = [];

  /** Cached deduped error messages for the TILES error banner. */
  public tilesErrorMessages: string[] = [];

  private _dismissedBanner = false;
  private _validationFailedFiles: PhxFileRejection[] = [];
  private _errorBannerReason: string = '';
  private _dropZoneSubtitleFormats: string = '';
  private _dropZoneSubtitleMaxSize: string = '';
  private _acceptedFileTypes: string = '';
  private _displayFiles: PhxFile[] = [];
  private readonly _destroy$ = new Subject<void>();
  private readonly _uploadSubscriptions = new Map<string, Subscription>();
  private _fileStatusSubscription?: Subscription;
  private _onChange: (files: PhxFile[]) => void = () => { };
  private _onTouched: () => void = () => { };

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _phxFileUploadS3Service: PhxFileUploadS3Service,
    private readonly _phxTranslate: PhxTranslatePipe
  ) {
    this._acceptedFileTypes = this.computeAcceptedFileTypes();
    this.updateDropZoneSubtitles();
  }

  public get dropZoneText(): string {
    return this.phxDropText ?? this._phxTranslate.transform('Drag and drop images & documents');
  }

  public get uploadButtonText(): string {
    return this.phxUploadBtnText ?? this._phxTranslate.transform('Upload');
  }

  public get tilesErrorTitle(): string {
    return this.phxTilesErrorTitle ?? this._phxTranslate.transform('Uploaded files have the following issues:');
  }

  public get fileLimitText(): string {
    if (!this.phxFileUploadLimit) return '';
    return `${this._phxTranslate.transform('You can only upload up to')} ${this.phxFileUploadLimit} ${this.phxFileUploadLimit === 1 ? this._phxTranslate.transform('file') : this._phxTranslate.transform('files')}.`;
  }

  public get showDropZone(): boolean {
    if (this.phxHideUploadContainer) {
      return false;
    }
    if (this.phxLayout === PHX_LAYOUT_TYPE.TILES) {
      return this.phxEditMode && !this.isLimitReached;
    }
    return (this.phxFiles.length < 1 || this.phxFileBulkUploadMode) && this.phxEditMode && this.phxLayout === PHX_LAYOUT_TYPE.WRAPPED;
  }

  /** Icon shown in an empty capture tile, matching the accepted file type. */
  public get placeholderIcon(): string {
    switch (this.phxFilesTypesAllowed) {
      case PHX_FILE_TYPE.VIDEOS:
        return Video;
      case PHX_FILE_TYPE.IMAGES:
        return Image;
      default:
        return Document;
    }
  }

  public get showTilesErrorBanner(): boolean {
    return this.phxLayout === PHX_LAYOUT_TYPE.TILES && this.tilesErrorMessages.length > 0;
  }

  /**
   * Success banner is opt-in: only shown when the consumer supplies phxTilesSuccessMessage and the
   * tiles are filled with files who are in DONE status.
   */
  public get showTilesSuccessBanner(): boolean {
    return this.phxLayout === PHX_LAYOUT_TYPE.TILES && !!this.phxTilesSuccessMessage && this.isTilesCaptureComplete;
  }

  /** True when every tile slot (up to phxFileUploadLimit) is filled and all files are DONE. */
  private get isTilesCaptureComplete(): boolean {
    if (this.phxFiles.length === 0) {
      return false;
    }
    if (this.phxFileUploadLimit != null && this.phxFiles.length < this.phxFileUploadLimit) {
      return false;
    }
    return this.phxFiles.every(file => file.status$?.getValue() === PHX_FILE_LOADING_STATUS.DONE);
  }

  public get showUploadButton(): boolean {
    return !this.phxHideUploadContainer && this.phxLayout === PHX_LAYOUT_TYPE.WRAPPED && this.phxEditMode;
  }

  public get isLimitReached(): boolean {
    return this.phxFileUploadLimit !== undefined && this.phxFiles.length >= this.phxFileUploadLimit;
  }

  public get displayFiles(): PhxFile[] {
    return this._displayFiles;
  }

  /** Aggregated validation/banner errors (unsupported format, size, empty) */
  public get validationErrors(): PhxFile[] {
    return this.phxFiles.filter(f => [PhxFileErrorCode.UNSUPPORTED_FORMAT, PhxFileErrorCode.UNSUPPORTED_SIZE, PhxFileErrorCode.EMPTY_FILE, PhxFileErrorCode.CORRUPT_FILE].includes(f.errorCode as PhxFileErrorCode));
  }

  public get showErrorBanner(): boolean {
    return !this._dismissedBanner && this.phxEditMode && this.currentSelectionRejectionCount > 0 && this.phxLayout === PHX_LAYOUT_TYPE.WRAPPED;
  }

  public get errorBannerTitle(): string {
    const count = this.currentSelectionRejectionCount;
    if (count === 1) {
      return this._phxTranslate.transform('Upload failed');
    }
    return `${count} ${this._phxTranslate.transform('Uploads failed')}`;
  }

  public get errorBannerReason(): string {
    return this._errorBannerReason;
  }

  public get fileCountText(): string {
    const uploadedFilesCount = this.phxFiles.filter(file => file.status$?.getValue() === PHX_FILE_LOADING_STATUS.DONE).length;
    const totalFilesCount = this.phxFiles.length;
    if (this.phxShowBatchProgress) {
      return `${uploadedFilesCount} ${this._phxTranslate.transform('of')} ${totalFilesCount} ${this._phxTranslate.transform('uploaded')}`;
    }
    return `${uploadedFilesCount} ${uploadedFilesCount === 1 ? this._phxTranslate.transform('file') : this._phxTranslate.transform('files')} ${this._phxTranslate.transform('uploaded')}`;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxFiles, phxFilesTypesAllowed, phxAllowedFormats, phxFileMaxSize, phxEditMode, phxFileUploadLimit, phxLayout } = changes;

    if (phxFiles || phxEditMode) {
      this.updateDisplayFiles();
    } else if (phxFileUploadLimit || phxLayout) {
      this.recomputeTileState();
    }

    if (phxFiles) {
      this.updateGalleryImages();
      this.subscribeToFileStatuses();
    }
    if (phxFilesTypesAllowed || phxAllowedFormats) {
      this._acceptedFileTypes = this.computeAcceptedFileTypes();
      this.updateDropZoneSubtitles();
    }
    if (phxFileMaxSize) {
      this._dropZoneSubtitleMaxSize = `${this._phxTranslate.transform('File Size:')} ${this.phxFileMaxSize} ${this._phxTranslate.transform('MB Max')}`;
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._fileStatusSubscription?.unsubscribe();
  }

  public writeValue(files: PhxFile[]): void {
    this.phxFiles = files || [];
    this.updateDisplayFiles();
    this.updateGalleryImages();
    this.subscribeToFileStatuses();
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: (files: PhxFile[]) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cdr.markForCheck();
  }

  public async onFileInputChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      await this.handleFiles(Array.from(input.files));
    }
    input.value = '';
  }

  public async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      await this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  public onFileRemove(event: { file: PhxFile; index: number }): void {
    const { file, index } = event;
    if (index >= 0 && index < this.phxFiles.length) {
      this.cancelFileUpload(file);
      this.revokeBlobUrl(file);
      this.phxFiles.splice(index, 1);
      this.updateDisplayFiles();
      this.updateGalleryImages();
      this.phxFilesChange.emit([...this.phxFiles]);
      this.phxDeleteFile.emit({ file, index });
      this._onChange([...this.phxFiles]);
      this._cdr.markForCheck();
    }
  }

  public onFileRetry(event: PhxFileEventPayload): void {
    const { file, index } = event;
    if (file.errorCode === PhxFileErrorCode.FETCH_FAILED) {
      file.error = undefined;
      file.errorCode = undefined;
      file.errorMessage = undefined;
      file.retryable = false;
      file.status$?.next(PHX_FILE_LOADING_STATUS.DONE);
      this._cdr.markForCheck();
      return;
    }
    if (this.phxUploadOnSelect && this.phxUploadURLProvider) {
      file.progress$?.next(0);
      file.status$?.next(PHX_FILE_LOADING_STATUS.START);
      file.error = undefined;
      file.errorCode = undefined;
      file.errorMessage = undefined;
      file.retryable = false;
      file.blocking = false;
      this._cdr.markForCheck();
      this.uploadFile(file);
    }
    this.updateGalleryImages();
    this.phxFileRetry.emit({ file, index });
  }

  /**
   * Opens the image gallery at the selected file index.
   * Only opens gallery for image files (png, jpg, jpeg).
   * For PDF files, opens them in a new tab.
   * Receives the file and its index directly from phx-file-item.
   */
  public onFilePreview(event: { file: PhxFile; index: number }): void {
    const { file, index } = event;

    if (file.status$?.getValue() === PHX_FILE_LOADING_STATUS.ERROR) {
      return; // Don't open the gallery if the file is in error state
    }
    if (this.phxLayout !== PHX_LAYOUT_TYPE.INLINE_NO_DETAIL && !this.phxObfuscate) {
      if (['pdf', 'geojson', 'json'].includes(file.type ?? '') && file.url) {
        // TODO: Opening the preview for the pdf that is just uploaded and the url is a blob does not work | Same issue can be observed currently in webapp.
        window.open(file.url, '_blank');
      } else if (this.isImageFile(file)) {
        // Calculate the gallery index by counting image files before this index
        let originalIndex = index;
        if (!this.phxEditMode) {
          originalIndex = this.phxFiles.findIndex(f => this.isSameFile(f, file));
        }

        const galleryIndex = this.phxFiles
          .slice(0, originalIndex)
          .filter(f => this.isImageFile(f) && f.status$?.getValue() !== PHX_FILE_LOADING_STATUS.ERROR)
          .length;

        // TODO: Investigate why navigating to first file via arrow keys when gallery is open is causing that file to open no matter which file we click after closing gallery
        this.gallerySelectedIndex = galleryIndex;
        this.isGalleryOpen = true;
        this._cdr.markForCheck();
      }
    }
    this.phxFileClicked.emit({ file, index });
  }

  public get acceptedFileTypes(): string {
    return this._acceptedFileTypes;
  }

  public dismissErrorBanner(): void {
    this._dismissedBanner = true;
  }

  public onBlur(): void {
    this._onTouched();
  }

  /** Returns true if the given file matches the active file */
  public isActive(file: PhxFile): boolean {
    if (!this.phxActiveFile) return false;
    return this.isSameFile(this.phxActiveFile, file);
  }

  public trackByFile(index: number, file: PhxFile): string | number {
    return file.offline_id || file.id || index;
  }

  public trackByTileSlot(index: number, slot: PhxFile | null): string | number {
    return slot ? slot.offline_id || slot.id || index : `placeholder-${index}`;
  }

  /**
   * Handles a click on an empty capture tile. Always emits phxTileCapture; when
   * phxShowPickerOnTileClick is true, also opens the native file picker.
   */
  public onTileCapture(index: number): void {
    if (!this.phxEditMode || this.isLimitReached) {
      return;
    }
    this.phxTileCapture.emit({ index });
    if (this.phxShowPickerOnTileClick) {
      this._fileInput?.nativeElement.click();
    }
  }

  // TODO: Implement the logic to gracefully handle the error during image deletion
  public onGalleryDeleteImage(galleryIndex: number): void {
    if (galleryIndex >= 0 && galleryIndex < this.galleryImages.length) {
      // Find the corresponding file in phxFiles using the gallery image
      const imageToDelete = this.galleryImages[galleryIndex];
      const fileToDelete = this.phxFiles.find(f => f.url === imageToDelete.src);

      if (fileToDelete) {
        const indexToDelete = this.phxFiles.findIndex(f => this.isSameFile(f, fileToDelete));
        this.onFileRemove({ file: fileToDelete, index: indexToDelete });

        // Close gallery if no more images
        if (this.galleryImages.length === 0) {
          this.isGalleryOpen = false;
        } else if (galleryIndex >= this.galleryImages.length) {
          // Adjust selected index if we deleted the last image
          this.gallerySelectedIndex = this.galleryImages.length - 1;
        }
        this._cdr.markForCheck();
      }
    }
  }

  public onGalleryRotateImage({ rotatedImage, index }: { rotatedImage: string, index: number }): void {
    const imageFiles = this.phxFiles.filter(file => this.isImageFile(file));
    if (this.gallerySelectedIndex >= 0 && this.gallerySelectedIndex < imageFiles.length) {
      const fileToUpdate = imageFiles[this.gallerySelectedIndex];
      const fileIndex = this.phxFiles.findIndex(f => this.isSameFile(f, fileToUpdate));
      if (fileIndex !== -1) {
        this.phxFiles[fileIndex].phxUpdatedFile = this.base64ToFile(rotatedImage, this.phxFiles[fileIndex].name);
        this.updateGalleryImages();
        this.phxFileUpdated.emit({ file: this.phxFiles[fileIndex], index: fileIndex });
        this._cdr.markForCheck();
      }
    }
  }

  public get dropZoneSubtitleFormats(): string {
    return this._dropZoneSubtitleFormats;
  }

  public get dropZoneSubtitleMaxSize(): string {
    return this._dropZoneSubtitleMaxSize;
  }

  private base64ToFile(base64Data: string, fileName: string): File {
    const parts = base64Data.split(',');

    // Validate that we have both parts of the data URL
    if (parts.length < 2 || !parts[1]) {
      throw new Error('Invalid base64 data URL format. Expected "data:mime/type;base64,<data>"');
    }

    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';

    const binaryString = atob(parts[1]);

    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return new File([bytes], fileName, { type: mime });
  }

  private get currentSelectionRejectionCount(): number {
    return this._validationFailedFiles.length;
  }

  private updateDisplayFiles(): void {
    if (this.phxEditMode) {
      this._displayFiles = this.phxFiles;
    } else {
      this._displayFiles = [...this.phxFiles].sort((a, b) => {
        const aIsImage = this.isImageFile(a);
        const bIsImage = this.isImageFile(b);
        if (aIsImage && !bIsImage) return -1;
        if (!aIsImage && bIsImage) return 1;
        return 0;
      });
    }
    this.recomputeTileState();
  }

  /** Rebuilds cached TILES layout state (slots and error banner messages). */
  private recomputeTileState(): void {
    const max = this.phxFileUploadLimit ?? this._displayFiles.length;
    const slots: (PhxFile | null)[] = [...this._displayFiles];
    if (this.phxEditMode) {
      while (slots.length < max) {
        slots.push(null);
      }
    }
    this.tileSlots = slots;

    if (this.phxLayout !== PHX_LAYOUT_TYPE.TILES) {
      this.tilesErrorMessages = [];
      return;
    }

    const messages: string[] = [];
    this.phxFiles
      .filter(file => file.status$?.getValue() === PHX_FILE_LOADING_STATUS.ERROR)
      .forEach(file => {
        if (file.errorMessages?.length) {
          messages.push(...file.errorMessages);
        } else if (file.errorMessage) {
          messages.push(file.errorMessage);
        }
      });
    this.tilesErrorMessages = Array.from(new Set(messages));
  }

  private async handleFiles(files: File[]): Promise<void> {
    this.resetSelectionBatch();
    let availableSlots = this.phxFileUploadLimit !== undefined
      ? Math.max(0, this.phxFileUploadLimit - this.phxFiles.length)
      : undefined;

    let limitExceededErrorShown = false;

    for (const file of files) {
      if (availableSlots !== undefined && availableSlots <= 0) {
        limitExceededErrorShown = this.handleFileLimitExceeded(file, limitExceededErrorShown);
        continue;
      }

      const validation = this.validateFile(file);
      if (!validation.valid) {
        this.handleInvalidFile(file, validation);
        continue;
      }

      if (await this.isCorruptImage(file)) {
        this.handleCorruptFile(file);
        continue;
      }

      this.processValidFile(file);

      if (availableSlots !== undefined) {
        availableSlots--;
      }
    }

    this.finalizeFileProcessing();
  }

  /** Records a limit-exceeded error once per batch. Returns true once the error has been shown. */
  private handleFileLimitExceeded(file: File, alreadyShown: boolean): boolean {
    if (!alreadyShown) {
      this._validationFailedFiles.push({
        name: file.name,
        size: file.size,
        code: PhxFileErrorCode.LIMIT_EXCEEDED,
        message: this.fileLimitText,
      });
      this.updateErrorBannerReason();
    }
    return true;
  }

  /** Routes a validation failure to the appropriate error UI based on layout type and error code. */
  private handleInvalidFile(file: File, validation: { valid: boolean; errorCode?: PhxFileErrorCode; errorMessage?: string }): void {
    if (this.phxLayout === PHX_LAYOUT_TYPE.INLINE_NO_DETAIL) {
      const phxFile = this.createPhxFile(file);
      this.setFileError(phxFile, validation.errorCode!, validation.errorMessage!, false, false);
      phxFile.status$?.next(PHX_FILE_LOADING_STATUS.ERROR);
      this.phxFiles.push(phxFile);
      this.phxFileError.emit(phxFile);
      return;
    }

    // Tiles layout: surface the failure as a red error tile and let the aggregated banner read it
    // from the file's error state (single source of truth shared with consumer-reported errors).
    if (this.phxLayout === PHX_LAYOUT_TYPE.TILES) {
      const phxFile = this.createPhxFile(file);
      this.setFileError(phxFile, validation.errorCode!, validation.errorMessage!, false, false);
      phxFile.errorMessages = validation.errorMessage ? [validation.errorMessage] : [];
      phxFile.status$?.next(PHX_FILE_LOADING_STATUS.ERROR);
      this.phxFiles.push(phxFile);
      this.phxFileError.emit(phxFile);
      return;
    }

    if (validation.errorCode === PhxFileErrorCode.UNSUPPORTED_FORMAT || validation.errorCode === PhxFileErrorCode.UNSUPPORTED_SIZE) {
      this._validationFailedFiles.push({
        name: file.name,
        size: file.size,
        code: validation.errorCode!,
        message: validation.errorMessage!,
      });
      this.updateErrorBannerReason();
      const temp: PhxFile = {
        name: file.name,
        url: '',
        type: this.getFileType(file.type),
        size: file.size,
        progress$: new BehaviorSubject<number>(0),
        status$: new BehaviorSubject<PHX_FILE_LOADING_STATUS>(PHX_FILE_LOADING_STATUS.ERROR),
        error: validation.errorMessage,
        errorCode: validation.errorCode,
        errorMessage: validation.errorMessage,
        retryable: false,
        blocking: false,
      };
      this.phxFileError.emit(temp);
      return;
    }

    if (validation.errorCode === PhxFileErrorCode.EMPTY_FILE) {
      const phxFile = this.createPhxFile(file);
      this.setFileError(phxFile, PhxFileErrorCode.EMPTY_FILE, validation.errorMessage!, false, false);
      phxFile.status$?.next(PHX_FILE_LOADING_STATUS.ERROR);
      this.phxFiles.push(phxFile);
      this.phxFileError.emit(phxFile);
    }
  }

  /** Creates a PhxFile for a corrupt image and emits an error. */
  private handleCorruptFile(file: File): void {
    const phxFile = this.createPhxFile(file);
    const corruptMessage = this._phxTranslate.transform('File is corrupted');
    this.setFileError(phxFile, PhxFileErrorCode.CORRUPT_FILE, corruptMessage, false, false);
    phxFile.status$?.next(PHX_FILE_LOADING_STATUS.ERROR);
    this.phxFiles.push(phxFile);
    this.phxFileError.emit(phxFile);
  }

  /** Creates a PhxFile, adds it to the list, emits selection, and optionally triggers upload. */
  private processValidFile(file: File): void {
    const phxFile = this.createPhxFile(file);
    this.phxFiles.push(phxFile);
    this.phxFileSelected.emit(phxFile);
    this._onChange([...this.phxFiles]);

    if (this.phxUploadOnSelect && this.phxUploadURLProvider) {
      this.uploadFile(phxFile);
    }
  }

  /** Updates display state and notifies consumers after all files in a batch have been processed. */
  private finalizeFileProcessing(): void {
    this.updateDisplayFiles();
    this.updateGalleryImages();
    this.subscribeToFileStatuses();
    this.phxFilesChange.emit([...this.phxFiles]);
    this._onChange([...this.phxFiles]);
    this._cdr.markForCheck();
  }

  private validateFile(file: File): { valid: boolean; errorMessage?: string; errorCode?: PhxFileErrorCode } {
    // File-type validation takes precedence over file-size validation: a wrong-type file should
    // report an unsupported-format error even when it also exceeds the size limit.
    const mimeTypes = this.getMimeTypeMappings();
    const allowedMimeKeys = Object.keys(mimeTypes);
    if (!file.type || !allowedMimeKeys.includes(file.type)) {
      const ext = this.getFileExtension(file.name);
      const acceptedExtensions = this.getAcceptedExtensions();
      const allowedExtensions = acceptedExtensions.map(e => `.${e}`);
      if (!ext || !allowedExtensions.includes(ext)) {
        const allowedFileExtensions = this.formatExtensions(acceptedExtensions);
        return { valid: false, errorMessage: `${ext || this._phxTranslate.transform('Unknown format')} ${this._phxTranslate.transform('is not supported. Please upload')} ${allowedFileExtensions}`, errorCode: PhxFileErrorCode.UNSUPPORTED_FORMAT };
      }
    }

    const maxSizeBytes = this.phxFileMaxSize * BYTES_IN_MB;
    if (file.size > maxSizeBytes) {
      return { valid: false, errorMessage: `${this._phxTranslate.transform('File exceeds')} ${this.phxFileMaxSize}${this._phxTranslate.transform('MB')} ${this._phxTranslate.transform('limit')}`, errorCode: PhxFileErrorCode.UNSUPPORTED_SIZE };
    }

    if (file.size === 0) {
      return { valid: false, errorMessage: this._phxTranslate.transform('File is empty or corrupted.'), errorCode: PhxFileErrorCode.EMPTY_FILE };
    }

    return { valid: true };
  }

  private createPhxFile(file: File): PhxFile {
    const offline_id = generateUuid();
    const blobUrl = URL.createObjectURL(file);
    return {
      offline_id,
      name: file.name,
      url: blobUrl,
      type: this.getFileType(file.type),
      size: file.size,
      originalFile: file,
      progress$: new BehaviorSubject<number>(0),
      status$: new BehaviorSubject<PHX_FILE_LOADING_STATUS>(PHX_FILE_LOADING_STATUS.START),
    };
  }

  private uploadFile(file: PhxFile): void {
    if (!this.phxUploadURLProvider) {
      return;
    }
    file.progress$.next(0);

    this._cdr.markForCheck();
    const sub = this.phxUploadURLProvider(file)
      .pipe(
        switchMap(uploadLink => {
          if (uploadLink.id) file.id = uploadLink.id;
          const correlationId = file.offline_id || String(file.id);
          return this._phxFileUploadS3Service.uploadFile(uploadLink.upload_url, file.originalFile!, correlationId);
        }),
        takeUntil(this._destroy$),
        finalize(() => {
          const uploadKey = this.getUploadSubscriptionKey(file);
          if (uploadKey) {
            this._uploadSubscriptions.delete(uploadKey);
          }
        })
      )
      .subscribe({
        next: event => {
          if (event.type === HttpEventType.UploadProgress) {
            if (file.progress$) {
              file.progress$.next(event.progress || 0);
              file.status$.next(PHX_FILE_LOADING_STATUS.PROGRESS);
            }
          }
          if (event.type === HttpEventType.Response) {
            if (file.progress$) {
              file.progress$.next(100);
              file.status$.next(PHX_FILE_LOADING_STATUS.DONE);
            }
            this.phxUploadComplete.emit(file);
          }
          this._cdr.markForCheck();
        },
        error: err => {
          const isNetwork = err.status === 0 || navigator.onLine === false;
          const errorCode = isNetwork ? PhxFileErrorCode.NETWORK : PhxFileErrorCode.SERVER;
          const errorMessage = isNetwork ? this._phxTranslate.transform('Network error') : this._phxTranslate.transform('Upload failed');
          this.setFileError(file, errorCode, errorMessage, true, true);
          file.progress$?.next(0);
          file.status$?.next(PHX_FILE_LOADING_STATUS.ERROR);
          this.phxFileError.emit(file);
          this._cdr.markForCheck();
        }
      });
    const uploadKey = this.getUploadSubscriptionKey(file);
    if (uploadKey) {
      this._uploadSubscriptions.set(uploadKey, sub);
    }
  }

  private cancelFileUpload(file: PhxFile): void {
    const uploadKey = this.getUploadSubscriptionKey(file);
    if (!uploadKey) {
      return;
    }

    const sub = this._uploadSubscriptions.get(uploadKey);
    if (sub) {
      sub.unsubscribe();
      this._uploadSubscriptions.delete(uploadKey);
    }
  }

  private getUploadSubscriptionKey(file: PhxFile): string | undefined {
    return file.offline_id;
  }

  private setFileError(file: PhxFile, code: PhxFileErrorCode, message: string, retryable: boolean, blocking: boolean): void {
    file.errorCode = code;
    file.errorMessage = message;
    file.error = message;
    file.retryable = retryable;
    file.blocking = blocking;
    if (code !== undefined) {
      file.status$?.next(PHX_FILE_LOADING_STATUS.ERROR);
    }
  }


  /**
   * Resolves the active formats. When phxAllowedFormats is provided it is authoritative and fully
   * defines the accepted formats (composed straight from the master registry, so consumers can mix
   * formats across categories). When omitted, the selected type's default format set is used.
   */
  private getActiveFormats(): PHX_FILE_FORMAT[] {
    if (this.phxAllowedFormats?.length) {
      return this.phxAllowedFormats.filter(format => Object.prototype.hasOwnProperty.call(PHX_FILE_FORMAT_MIME, format));
    }
    return PHX_TYPE_DEFAULT_FORMATS[this.phxFilesTypesAllowed] ?? [];
  }

  /**
   * User-facing extension list for the active formats (one entry per format). Derived from the
   * formats rather than the MIME map so shared MIME aliases (e.g. image/jpeg for both jpg and jpeg)
   * do not produce duplicate or overwritten extensions.
   */
  private getAcceptedExtensions(): string[] {
    const extensions = this.getActiveFormats()
      .map(format => PHX_FILE_FORMAT_MIME[format]?.ext)
      .filter((ext): ext is string => !!ext);
    return Array.from(new Set(extensions));
  }

  /**
   * Builds the `accept` attribute value from the active formats. Cached in `_acceptedFileTypes`
   * and recomputed only when the driving inputs change
   */
  private computeAcceptedFileTypes(): string {
    const mimeKeys = Object.keys(this.getMimeTypeMappings());
    const extensions = this.getAcceptedExtensions().map(ext => `.${ext}`);
    return Array.from(new Set([...mimeKeys, ...extensions])).join(',');
  }

  private getMimeTypeMappings(): Record<string, string> {
    return this.getActiveFormats().reduce<Record<string, string>>((mappings, format) => {
      const entry = PHX_FILE_FORMAT_MIME[format];
      if (entry) {
        entry.mimes.forEach(mime => {
          mappings[mime] = entry.ext;
        });
      }
      return mappings;
    }, {});
  }

  private getFileType(mimeType: string): string {
    const mimeTypes = this.getMimeTypeMappings();
    return mimeTypes[mimeType] || 'unknown';
  }

  private getFileExtension(name: string): string {
    const lastDot = name.lastIndexOf('.');
    if (lastDot === -1 || lastDot === name.length - 1) return '';
    return name.substring(lastDot).toLowerCase();
  }

  private isSameFile(file1: PhxFile, file2: PhxFile): boolean {
    return file1.url === file2.url;
  }

  private resetSelectionBatch(): void {
    this._validationFailedFiles = [];
    this._errorBannerReason = '';
    this._dismissedBanner = false;
  }

  private updateErrorBannerReason(): void {
    const reasons = this.collectRejectionReasons();
    this._errorBannerReason = Array.from(reasons).join(' | ');
  }

  private collectRejectionReasons(): Set<string> {
    const set = new Set<string>();
    this._validationFailedFiles.forEach(err => set.add(err.message));
    return set;
  }

  private updateDropZoneSubtitles(): void {
    this._dropZoneSubtitleFormats = `${this._phxTranslate.transform('Supported formats:')} ${this.getAcceptedExtensions().join(', ')}`;
    this._dropZoneSubtitleMaxSize = `${this._phxTranslate.transform('File Size:')} ${this.phxFileMaxSize} ${this._phxTranslate.transform('MB Max')}`;
  }

  private formatExtensions(extensions: string[]): string {
    if (extensions.length === 0) {
      return '';
    }
    if (extensions.length === 1) {
      return extensions[0];
    }
    if (extensions.length === 2) {
      return `${extensions[0]} ${this._phxTranslate.transform('and')} ${extensions[1]}`;
    }
    const allButLast = extensions.slice(0, -1);
    const last = extensions[extensions.length - 1];
    return `${allButLast.join(', ')} ${this._phxTranslate.transform('or')} ${last}`;
  }

  private updateGalleryImages(): void {
    this.galleryImages = this.phxFiles
      .filter(file => this.isImageFile(file) && file.status$?.getValue() !== PHX_FILE_LOADING_STATUS.ERROR)
      .map(file => ({
        title: file.name,
        src: file.url,
        type: file.type,
      }));
  }

  /**
   * Subscribes to all file status$ BehaviorSubjects to react when any file enters error or done state.
   */
  private subscribeToFileStatuses(): void {
    this._fileStatusSubscription?.unsubscribe();

    const statusObservables = this.phxFiles
      .filter(file => file.status$)
      .map(file => file.status$!.pipe(
        filter(status => status === PHX_FILE_LOADING_STATUS.ERROR || status === PHX_FILE_LOADING_STATUS.DONE)
      ));

    if (statusObservables.length === 0) {
      return;
    }

    this._fileStatusSubscription = merge(...statusObservables)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.recomputeTileState();
        this.updateGalleryImages();
        this._cdr.markForCheck();
      });
  }

  private isImageFile(file: PhxFile): boolean {
    return this.imageTypes.includes(file.type);
  }

  private revokeBlobUrl(file: PhxFile): void {
    if (typeof file.url === 'string' && file.url.startsWith('blob:')) {
      URL.revokeObjectURL(file.url);
    }
  }

  private async isCorruptImage(file: File): Promise<boolean> {
    const imageMimeTypes = Object.keys(PHX_IMAGE_MIME_MAPPINGS);
    if (!imageMimeTypes.includes(file.type)) {
      return false;
    }

    try {
      const bitmap = await createImageBitmap(file);
      const isCorrupt = bitmap.width === 0 || bitmap.height === 0;
      bitmap.close();
      return isCorrupt;
    } catch {
      return true;
    }
  }
}
